import { IMediaRecorder, MediaRecorder as ExtendableMediaRecorder, register } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

export type ReactMediaRecorderRenderProps = {
  error: string;
  muteAudio: () => void;
  unMuteAudio: () => void;
  startRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => void;
  mediaBlobUrl: undefined | string;
  status: StatusMessages;
  isAudioMuted: boolean;
  previewStream: MediaStream | null;
  previewAudioStream: MediaStream | null;
  clearBlobUrl: () => void;
};

export type ReactMediaRecorderHookProps = {
  audio?: boolean | MediaTrackConstraints;
  video?: boolean | MediaTrackConstraints;
  screen?: boolean;
  onStop?: (blobUrl: string, blob: Blob) => void;
  onStart?: () => void;
  blobPropertyBag?: BlobPropertyBag;
  mediaRecorderOptions?: MediaRecorderOptions | undefined;
  customMediaStream?: MediaStream | null;
  stopStreamsOnStop?: boolean;
  askPermissionOnMount?: boolean;
  onChunkCaptured?: (chunk: Blob, accumulatedSizeBytes: number) => void;
  preferRecordedMimeType?: boolean;
};
export type ReactMediaRecorderProps = ReactMediaRecorderHookProps & {
  render: (props: ReactMediaRecorderRenderProps) => ReactElement;
};

export type StatusMessages =
  | 'media_aborted'
  | 'permission_denied'
  | 'no_specified_media_found'
  | 'media_in_use'
  | 'invalid_media_constraints'
  | 'no_constraints'
  | 'recorder_error'
  | 'idle'
  | 'acquiring_media'
  | 'delayed_start'
  | 'recording'
  | 'stopping'
  | 'stopped'
  | 'paused';

export enum RecorderErrors {
  AbortError = 'media_aborted',
  NotAllowedError = 'permission_denied',
  NotFoundError = 'no_specified_media_found',
  NotReadableError = 'media_in_use',
  OverconstrainedError = 'invalid_media_constraints',
  TypeError = 'no_constraints',
  NONE = '',
  NO_RECORDER = 'recorder_error'
}

let isEncoderRegistered = false;
const CHUNK_CAPTURE_TIMESLICE_MS = 1000;

const isIpadSafari = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = window.navigator.userAgent || '';
  const isSafari = /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome/i.test(userAgent);
  const isIpadLike = /iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && window.navigator.maxTouchPoints > 1);

  return isSafari && isIpadLike;
};

export function useReactMediaRecorder({
  audio = true,
  video = false,
  onStop = () => null,
  onStart = () => null,
  blobPropertyBag,
  screen = false,
  mediaRecorderOptions = undefined,
  customMediaStream = null,
  stopStreamsOnStop = true,
  askPermissionOnMount = false,
  onChunkCaptured,
  preferRecordedMimeType = false
}: ReactMediaRecorderHookProps): ReactMediaRecorderRenderProps {
  const mediaRecorder = useRef<IMediaRecorder | null>(null);
  const mediaChunks = useRef<Blob[]>([]);
  const mediaStream = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<StatusMessages>('idle');
  const [isAudioMuted, setIsAudioMuted] = useState<boolean>(false);
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | undefined>(undefined);
  const [error, setError] = useState<keyof typeof RecorderErrors>('NONE');
  const accumulatedChunkSizeRef = useRef(0);
  const chunkRequestIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chunkMimeTypesRef = useRef<Set<string>>(new Set());

  const clearChunkRequestInterval = useCallback(() => {
    if (chunkRequestIntervalRef.current) {
      clearInterval(chunkRequestIntervalRef.current);
      chunkRequestIntervalRef.current = null;
    }
  }, []);

  const cleanupMediaStream = useCallback(() => {
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStream.current = null;
    }
  }, []);
  const startRecording = async () => {
    setError('NONE');
    mediaChunks.current = [];
    chunkMimeTypesRef.current = new Set();
    if (!mediaStream.current) {
      await getMediaStream();
    }
    if (mediaStream.current) {
      const isStreamEnded = mediaStream.current.getTracks().some((track) => track.readyState === 'ended');
      if (isStreamEnded) {
        await getMediaStream();
      }

      // User blocked the permissions (getMediaStream errored out)
      if (!mediaStream.current.active) {
        return;
      }
      accumulatedChunkSizeRef.current = 0;
      mediaRecorder.current = new ExtendableMediaRecorder(mediaStream.current, mediaRecorderOptions || undefined);
      mediaRecorder.current.ondataavailable = onRecordingActive;
      mediaRecorder.current.onstop = onRecordingStop;
      mediaRecorder.current.onstart = onRecordingStart;
      mediaRecorder.current.onerror = () => {
        setError('NO_RECORDER');
        setStatus('idle');
      };
      if (onChunkCaptured) {
        mediaRecorder.current.start(CHUNK_CAPTURE_TIMESLICE_MS);
        clearChunkRequestInterval();
        chunkRequestIntervalRef.current = setInterval(() => {
          if (!mediaRecorder.current || mediaRecorder.current.state !== 'recording') {
            return;
          }

          try {
            const recorderWithChunkRequest = mediaRecorder.current as IMediaRecorder & { requestData?: () => void };
            recorderWithChunkRequest.requestData?.();
          } catch (e) {
            console.warn('Failed to request media recorder chunk data:', e);
          }
        }, CHUNK_CAPTURE_TIMESLICE_MS);
      } else {
        mediaRecorder.current.start();
      }
      setStatus('recording');
    }
  };

  const onRecordingActive = ({ data }: BlobEvent) => {
    const chunkSize = typeof data?.size === 'number' && Number.isFinite(data.size) ? Math.max(0, data.size) : 0;
    const chunkType = typeof data?.type === 'string' ? data.type : '';
    if (chunkType) {
      chunkMimeTypesRef.current.add(chunkType);
    }

    if (isIpadSafari() && accumulatedChunkSizeRef.current === 0) {
      console.info('[ReactMediaRecorder][iPad] recorder chunk captured', {
        chunkType,
        chunkSize,
        accumulatedSizeBytes: accumulatedChunkSizeRef.current + chunkSize
      });
    }

    accumulatedChunkSizeRef.current += chunkSize;
    if (onChunkCaptured) {
      onChunkCaptured(data, accumulatedChunkSizeRef.current);
    }
    mediaChunks.current.push(data);
  };

  const onRecordingStart = () => {
    onStart();
  };

  const onRecordingStop = () => {
    clearChunkRequestInterval();
    const [chunk] = mediaChunks.current;
    const recorderMimeType = mediaRecorder.current?.mimeType || '';
    const chunkType = chunk?.type || '';
    const configuredMimeType = mediaRecorderOptions?.mimeType || '';
    const explicitBlobType = blobPropertyBag?.type || '';
    const resolvedBlobType = preferRecordedMimeType
      ? chunkType || recorderMimeType || explicitBlobType || configuredMimeType
      : explicitBlobType || chunkType || recorderMimeType || configuredMimeType;
    const compatibilityFallbackBlobType = !preferRecordedMimeType ? (video ? 'video/mp4' : 'audio/wav') : '';
    const finalBlobType = resolvedBlobType || compatibilityFallbackBlobType;
    const blob = finalBlobType
      ? new Blob(mediaChunks.current, {
          ...(blobPropertyBag || {}),
          type: finalBlobType
        })
      : new Blob(mediaChunks.current, blobPropertyBag);

    if (isIpadSafari()) {
      console.info('[ReactMediaRecorder][iPad] recorder finalized', {
        recorderMimeType,
        chunkMimeTypes: Array.from(chunkMimeTypesRef.current),
        resolvedBlobType,
        compatibilityFallbackBlobType,
        finalBlobType,
        createdBlobType: blob.type,
        finalBlobSize: blob.size
      });
    }

    const url = URL.createObjectURL(blob);
    setStatus('stopped');
    setMediaBlobUrl(url);
    try {
      onStop(url, blob);
    } finally {
      if (stopStreamsOnStop) {
        cleanupMediaStream();
      }
      mediaRecorder.current = null;
      mediaChunks.current = [];
      chunkMimeTypesRef.current = new Set();
      accumulatedChunkSizeRef.current = 0;
    }
  };

  const muteAudio = (mute: boolean) => {
    setIsAudioMuted(mute);
    if (mediaStream.current) {
      mediaStream.current.getAudioTracks().forEach((audioTrack) => (audioTrack.enabled = !mute));
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      setStatus('paused');
      mediaRecorder.current.pause();
    }
  };
  const resumeRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'paused') {
      setStatus('recording');
      mediaRecorder.current.resume();
    }
  };

  const stopRecording = useCallback(() => {
    clearChunkRequestInterval();
    if (mediaRecorder.current) {
      if (mediaRecorder.current.state !== 'inactive') {
        setStatus('stopping');
        mediaRecorder.current.stop();
      }
    }
  }, [clearChunkRequestInterval]);

  useEffect(() => {
    const setup = async () => {
      if (!isEncoderRegistered) {
        try {
          await register(await connect());
          isEncoderRegistered = true;
        } catch (e: any) {
          if (e.message?.includes('already an encoder stored')) {
            isEncoderRegistered = true;
          } else {
            console.warn('Encoder registration failed:', e);
          }
        }
      }
    };
    setup();
  }, []);

  const getMediaStream = useCallback(async () => {
    setStatus('acquiring_media');
    const requiredMedia: MediaStreamConstraints = {
      audio,
      video
    };
    try {
      if (customMediaStream) {
        mediaStream.current = customMediaStream;
      } else if (screen) {
        const stream = (await window.navigator.mediaDevices.getDisplayMedia({
          video: video || true
        })) as MediaStream;
        stream.getVideoTracks()[0].addEventListener('ended', () => {
          stopRecording();
        });
        if (audio) {
          const audioStream = await window.navigator.mediaDevices.getUserMedia({
            audio
          });

          audioStream.getAudioTracks().forEach((audioTrack) => stream.addTrack(audioTrack));
        }
        mediaStream.current = stream;
      } else {
        mediaStream.current = await window.navigator.mediaDevices.getUserMedia(requiredMedia);
      }
      setStatus('idle');
    } catch (error: any) {
      setError(error.name);
      setStatus('idle');
    }
  }, [audio, video, customMediaStream, screen, stopRecording]);

  useEffect(() => {
    if (!window.MediaRecorder) {
      throw new Error('Unsupported Browser');
    }

    if (screen) {
      if (!window.navigator.mediaDevices.getDisplayMedia) {
        throw new Error("This browser doesn't support screen capturing");
      }
    }

    const checkConstraints = (mediaType: MediaTrackConstraints) => {
      const supportedMediaConstraints = navigator.mediaDevices.getSupportedConstraints();
      const unSupportedConstraints = Object.keys(mediaType).filter(
        (constraint) => !(supportedMediaConstraints as { [key: string]: any })[constraint]
      );

      if (unSupportedConstraints.length > 0) {
        console.error(
          `The constraints ${unSupportedConstraints.join(
            ','
          )} doesn't support on this browser. Please check your ReactMediaRecorder component.`
        );
      }
    };

    if (typeof audio === 'object') {
      checkConstraints(audio);
    }
    if (typeof video === 'object') {
      checkConstraints(video);
    }

    if (isEncoderRegistered && mediaRecorderOptions && mediaRecorderOptions.mimeType) {
      if (!ExtendableMediaRecorder.isTypeSupported(mediaRecorderOptions.mimeType)) {
        console.error(`The specified MIME type you supplied for MediaRecorder doesn't support this browser`);
      }
    }

    if (!mediaStream.current && askPermissionOnMount) {
      getMediaStream();
    }
  }, [audio, screen, video, getMediaStream, mediaRecorderOptions, askPermissionOnMount]);

  useEffect(() => {
    return () => {
      clearChunkRequestInterval();
    };
  }, [clearChunkRequestInterval]);

  return {
    error: RecorderErrors[error],
    muteAudio: () => muteAudio(true),
    unMuteAudio: () => muteAudio(false),
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    mediaBlobUrl,
    status,
    isAudioMuted,
    previewStream: mediaStream.current ? new MediaStream(mediaStream.current.getVideoTracks()) : null,
    previewAudioStream: mediaStream.current ? new MediaStream(mediaStream.current.getAudioTracks()) : null,
    clearBlobUrl: () => {
      if (mediaBlobUrl) {
        URL.revokeObjectURL(mediaBlobUrl);
      }
      accumulatedChunkSizeRef.current = 0;
      setMediaBlobUrl(undefined);
      setStatus('idle');
      cleanupMediaStream();
    }
  };
}

export const ReactMediaRecorder = (props: ReactMediaRecorderProps) => props.render(useReactMediaRecorder(props));
